import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "@/lib/api";
import { CAT_COLOR } from "@/lib/constants";

const pinIcon = (n, color, approx) => L.divIcon({
  className: "",
  html: `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};opacity:${approx ? 0.75 : 1};border:2px ${approx ? "dashed" : "solid"} #fff;box-shadow:0 2px 6px rgba(0,0,0,.35);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);color:#fff;font-weight:800;font-size:13px">${n}</span></div>`,
  iconSize: [30, 30], iconAnchor: [15, 30], tooltipAnchor: [0, -28],
});

const FitBounds = ({ points }) => {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView([points[0].lat, points[0].lng], 14);
    else if (points.length > 1) map.fitBounds(points.map((p) => [p.lat, p.lng]), { padding: [40, 40] });
  }, [points, map]);
  return null;
};

export const AgendaMap = ({ items, onOpen }) => {
  const [geo, setGeo] = useState(null);
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);
  const colorOf = (name) => (cats.find((c) => c.name === name) || {}).color || CAT_COLOR[name] || "#64748B";
  const legend = [...new Set(items.map((t) => t.category || "Senza categoria"))];
  const [shown, setShown] = useState([]);
  const catOf = (t) => t.category || "Senza categoria";
  const toggleCat = (c) => setShown((h) => (h.includes(c) ? h.filter((x) => x !== c) : [...h, c]));
  const visible = (c) => shown.length === 0 || shown.includes(c);
  const ids = items.map((t) => t.id).join(",");
  useEffect(() => {
    setGeo(null);
    if (!items.length) { setGeo({}); return; }
    api.post("/tickets/geo", { ids: items.map((t) => t.id) })
      .then((r) => setGeo(Object.fromEntries(r.data.map((g) => [g.id, g]))))
      .catch(() => setGeo({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  const points = useMemo(() => (geo ? items.map((t, i) => ({ t, n: i + 1, ...(geo[t.id] || {}) })).filter((p) => p.lat != null) : []), [geo, items]);
  const missing = geo ? items.filter((t) => !geo[t.id]) : [];

  if (geo === null) return <div className="flex h-[60vh] items-center justify-center rounded-xl border border-border bg-card text-sm text-muted-foreground" data-testid="agenda-map-loading">Localizzo gli indirizzi…</div>;
  return (
    <div className="space-y-2" data-testid="agenda-map">
      <div className="h-[62vh] overflow-hidden rounded-xl border border-border">
        <MapContainer center={[41.9, 12.5]} zoom={6} className="h-full w-full" scrollWheelZoom>
          <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <FitBounds points={points} />
          {points.filter((p) => visible(catOf(p.t))).map((p) => (
            <Marker key={p.t.id} position={[p.lat, p.lng]} icon={pinIcon(p.n, colorOf(p.t.category), p.approx)} eventHandlers={{ click: () => onOpen(p.t) }}>
              <Tooltip permanent direction="top" className="!rounded-md !px-1.5 !py-0.5 !text-[11px] !font-bold">
                {(p.t.id_olo || p.t.code)} · {p.t.start_time || p.t.appointment_time || "—"}
              </Tooltip>
            </Marker>
          ))}
        </MapContainer>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 rounded-lg border border-border bg-card px-3 py-2" data-testid="agenda-map-legend">
        {legend.map((c) => {
          const off = !visible(c);
          return (
            <button key={c} type="button" onClick={() => toggleCat(c)} data-testid={`agenda-legend-${c}`}
              className={`flex items-center gap-1.5 rounded-full px-1.5 py-0.5 text-xs font-semibold transition-opacity ${off ? "text-muted-foreground line-through opacity-50" : "text-navy"}`}>
              <span className="h-3 w-3 rounded-full" style={{ background: off ? "#cbd5e1" : colorOf(c) }} />{c}
            </button>
          );
        })}
        {shown.length > 0 && <button type="button" onClick={() => setShown([])} className="text-xs font-bold text-primary" data-testid="agenda-legend-all">Mostra tutte</button>}
      </div>
      {points.some((p) => p.approx) && <p className="text-xs text-muted-foreground">Pin tratteggiato: via non trovata, posizione indicativa sul comune.</p>}
      {missing.length > 0 && (
        <p className="text-xs text-muted-foreground" data-testid="agenda-map-missing">Non localizzati: {missing.map((t) => t.id_olo || t.code).join(", ")}</p>
      )}
    </div>
  );
};
